import React from 'react';
import { useLiveMatch } from '../hooks/useLiveMatch';
import { ConfirmModal } from './ConfirmModal';
import { SubstitutionModal } from './SubstitutionModal';
import { CaptainTransferModal } from './CaptainTransferModal';
import { VARReviewModal } from './VARReviewModal';
import { ConsoleButton } from './console/ConsoleButton';
import { TallyLight } from './console/TallyLight';
import { OperatorModeSelector } from './OperatorModeSelector';

const ConsolePlayerRow = ({ playerPos, onEventCreated }) => {
  const { player, eligibility, is_captain, jersey_number } = playerPos;
  const isInactive = !eligibility.eligible;

  return (
    <div className={`flex items-center gap-1 py-1 px-1.5 rounded-sm ${
      isInactive ? 'opacity-40' : 'hover:bg-gray-700/50'
    }`}>
      <span className={`font-mono text-xs w-6 text-right flex-shrink-0 ${isInactive ? 'text-gray-600' : 'text-gray-400'}`}>
        {jersey_number}
      </span>
      {is_captain && (
        <span className="text-[8px] font-bold text-amber-400 flex-shrink-0">C</span>
      )}
      <span className={`font-mono text-xs truncate flex-1 ${
        isInactive ? 'text-gray-600 line-through' : 'text-gray-200'
      }`}>
        {player.last_name}
      </span>
      {/* Status indicators */}
      {eligibility.isExpelled && (
        <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" title="Expelled" />
      )}
      {eligibility.isSubstituted && (
        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" title="Subbed" />
      )}
      {eligibility.yellowCards === 1 && !isInactive && (
        <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" title="1 Yellow" />
      )}
      {/* Action buttons */}
      {!isInactive && (
        <div className="flex gap-0.5 flex-shrink-0">
          <button
            onClick={() => onEventCreated('goal', playerPos)}
            className="w-7 h-7 rounded-sm bg-green-800 hover:bg-green-700 border border-green-600 text-[10px] font-bold text-green-200 active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]"
            title="Goal"
          >
            G
          </button>
          <button
            onClick={() => onEventCreated('yellow_card', playerPos)}
            className="w-7 h-7 rounded-sm bg-yellow-700 hover:bg-yellow-600 border border-yellow-500 text-[10px] font-bold text-yellow-200 active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]"
            title="Yellow Card"
          >
            Y
          </button>
          <button
            onClick={() => onEventCreated('red_card', playerPos)}
            className="w-7 h-7 rounded-sm bg-red-800 hover:bg-red-700 border border-red-600 text-[10px] font-bold text-red-200 active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]"
            title="Red Card"
          >
            R
          </button>
          <button
            onClick={() => onEventCreated('substitution', playerPos)}
            className="w-7 h-7 rounded-sm bg-blue-800 hover:bg-blue-700 border border-blue-600 text-[10px] font-bold text-blue-200 active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]"
            title="Sub"
          >
            S
          </button>
        </div>
      )}
    </div>
  );
};

const ConsoleTeamPanel = ({ players, label, color, counts, onEventCreated }) => {
  const activePlayers = players.filter(p =>
    p.eligibility.eligible &&
    !p.eligibility.isSubstituted &&
    !p.eligibility.isExpelled
  );
  const inactivePlayers = players.filter(p =>
    p.is_starting && (p.eligibility.isSubstituted || p.eligibility.isExpelled)
  );

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-sm flex flex-col h-full">
      <div className={`px-2 py-1.5 border-b border-gray-700 flex items-center justify-between bg-gray-800`}>
        <span className={`text-[10px] font-mono uppercase tracking-wider font-bold ${
          color === 'blue' ? 'text-blue-400' : 'text-green-400'
        }`}>
          {label}
        </span>
        {counts && (
          <span className="text-[10px] font-mono text-gray-500">
            {counts.active}/{counts.total}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-1 space-y-0.5">
        {activePlayers.map(p => (
          <ConsolePlayerRow key={p.id} playerPos={p} onEventCreated={onEventCreated} />
        ))}
        {inactivePlayers.length > 0 && (
          <>
            <div className="border-t border-gray-700 mt-1 pt-1">
              <span className="text-[8px] font-mono uppercase text-gray-600 px-1">OUT</span>
            </div>
            {inactivePlayers.map(p => (
              <ConsolePlayerRow key={p.id} playerPos={p} onEventCreated={onEventCreated} />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export const LiveMatchViewConsole = ({ matchId }) => {
  const m = useLiveMatch(matchId);

  if (!m.connected) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center">
          <div className="text-4xl mb-4 font-mono text-amber-400">CONNECTING...</div>
          <TallyLight state="warning" label="WS" />
        </div>
      </div>
    );
  }

  const halfLabel = m.clock.half === 1 ? '1ST' : m.clock.half === 2 ? '2ND' : m.clock.half === 3 ? 'ET1' : 'ET2';
  const matchStatusLabel = m.match?.status === 'live' ? 'LIVE' : m.match?.status === 'completed' ? 'FINAL' : m.match?.status === 'cancelled' ? 'CANCELLED' : 'PRE';

  return (
    <>
    <div className="h-screen bg-gray-900 text-gray-100 font-mono flex flex-col overflow-hidden select-none">
      {/* STATUS BAR */}
      <div className="h-12 bg-gray-800 border-b border-gray-700 px-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <span className="text-[10px] uppercase tracking-wider text-gray-500">Match #{matchId}</span>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm ${
            matchStatusLabel === 'LIVE' ? 'bg-red-700 text-white' :
            matchStatusLabel === 'FINAL' ? 'bg-blue-700 text-white' :
            matchStatusLabel === 'CANCELLED' ? 'bg-gray-600 text-gray-300' :
            'bg-gray-700 text-gray-400'
          }`}>
            {matchStatusLabel}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-gray-500">{halfLabel}</span>
          <span className="text-xl font-bold text-amber-400">
            {String(m.clock.minute).padStart(2, '0')}:{String(m.clock.second).padStart(2, '0')}
          </span>
          {m.clock.injuryTime > 0 && (
            <span className="text-sm font-bold text-red-400">+{m.clock.injuryTime}</span>
          )}
          <div className={`w-2 h-2 rounded-full ${m.clock.running ? 'bg-green-400 animate-pulse' : 'bg-gray-600'}`} />
        </div>
        <div className="flex items-center gap-4">
          <TallyLight state={m.connected ? 'connected' : 'disconnected'} label="WS" />
          <TallyLight state={m.joined ? 'connected' : 'idle'} label="ROOM" />
          <span className="text-[10px] text-gray-500">{m.viewers} VIEW</span>
          <OperatorModeSelector currentMode="console" matchId={matchId} />
        </div>
      </div>

      {/* MAIN GRID */}
      <div className="flex-1 grid grid-cols-[300px_1fr_300px] grid-rows-[1fr_220px] gap-px bg-gray-700 min-h-0">

        {/* LEFT: HOME TEAM */}
        <div className="row-span-1 min-h-0">
          <ConsoleTeamPanel
            players={m.homeTeam}
            label={m.match?.home_team?.short_name || m.match?.home_team?.name || 'HOME'}
            color="blue"
            counts={m.activeCounts?.homeTeam}
            onEventCreated={m.handleQuickEvent}
          />
        </div>

        {/* CENTER: SCORE + OVERLAY CONTROLS */}
        <div className="bg-gray-800 flex flex-col items-center justify-center gap-4 p-4 min-h-0">
          {/* Team names */}
          <div className="flex items-center gap-6 w-full justify-center">
            <span className="text-sm font-bold text-blue-400 uppercase tracking-wider truncate max-w-[200px]">
              {m.match?.home_team?.short_name || m.match?.home_team?.name || 'HOME'}
            </span>
            <span className="text-gray-600">VS</span>
            <span className="text-sm font-bold text-green-400 uppercase tracking-wider truncate max-w-[200px]">
              {m.match?.away_team?.short_name || m.match?.away_team?.name || 'AWAY'}
            </span>
          </div>

          {/* Score */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => m.handleScoreUpdate(m.score.home - 1, m.score.away)}
              className="w-8 h-8 rounded-sm bg-gray-700 border border-gray-600 text-gray-400 hover:bg-gray-600 text-xs font-bold"
            >
              -
            </button>
            <span className="text-6xl font-bold text-white">{m.score.home}</span>
            <span className="text-4xl text-gray-600 mx-2">:</span>
            <span className="text-6xl font-bold text-white">{m.score.away}</span>
            <button
              onClick={() => m.handleScoreUpdate(m.score.home, m.score.away - 1)}
              className="w-8 h-8 rounded-sm bg-gray-700 border border-gray-600 text-gray-400 hover:bg-gray-600 text-xs font-bold"
            >
              -
            </button>
          </div>

          {/* Score increment buttons */}
          <div className="flex items-center gap-6">
            <ConsoleButton
              onClick={() => m.handleScoreUpdate(m.score.home + 1, m.score.away)}
              color="blue"
              className="px-4 py-2"
            >
              HOME +1
            </ConsoleButton>
            <ConsoleButton
              onClick={() => m.handleScoreUpdate(m.score.home, m.score.away + 1)}
              color="green"
              className="px-4 py-2"
            >
              AWAY +1
            </ConsoleButton>
          </div>

          {/* Overlay Controls */}
          <div className="flex gap-2 mt-2">
            <ConsoleButton
              onClick={m.toggleScoreboard}
              color="red"
              active={m.scoreboardVisible}
              className="px-3 py-2"
            >
              SCOREBOARD
            </ConsoleButton>
            <ConsoleButton
              onClick={m.toggleFormations}
              color="amber"
              active={m.formationsVisible}
              className="px-3 py-2"
            >
              FORMATIONS
            </ConsoleButton>
            <ConsoleButton
              onClick={m.toggleLogo}
              color="blue"
              active={m.logoVisible}
              className="px-3 py-2"
            >
              LOGO
            </ConsoleButton>
          </div>

          {/* Match Status Actions */}
          <div className="flex gap-2 mt-2">
            {m.match?.status === 'scheduled' && (
              <ConsoleButton
                onClick={() => m.setConfirmModal({
                  isOpen: true,
                  action: 'start',
                  title: 'Start Match',
                  message: 'Start this match?'
                })}
                color="green"
                className="px-4 py-2"
              >
                START MATCH
              </ConsoleButton>
            )}
            {m.match?.status === 'live' && (
              <ConsoleButton
                onClick={() => {
                  if (m.clock.running) {
                    alert('Stop clock first');
                    return;
                  }
                  m.setConfirmModal({
                    isOpen: true,
                    action: 'end',
                    title: 'End Match',
                    message: 'End this match?'
                  });
                }}
                color="blue"
                className="px-4 py-2"
              >
                END MATCH
              </ConsoleButton>
            )}
            <ConsoleButton
              onClick={() => m.setConfirmModal({
                isOpen: true,
                action: 'cancel',
                title: 'Cancel Match',
                message: 'Cancel this match?'
              })}
              color="red"
              className="px-4 py-2"
            >
              CANCEL
            </ConsoleButton>
          </div>

          {/* External views */}
          <div className="flex gap-2">
            <ConsoleButton onClick={m.openPublicView} color="gray" className="px-3 py-1.5">
              PUBLIC
            </ConsoleButton>
            <ConsoleButton onClick={m.openOBSView} color="gray" className="px-3 py-1.5">
              OBS
            </ConsoleButton>
          </div>
        </div>

        {/* RIGHT: AWAY TEAM */}
        <div className="row-span-1 min-h-0">
          <ConsoleTeamPanel
            players={m.awayTeam}
            label={m.match?.away_team?.short_name || m.match?.away_team?.name || 'AWAY'}
            color="green"
            counts={m.activeCounts?.awayTeam}
            onEventCreated={m.handleQuickEvent}
          />
        </div>

        {/* BOTTOM LEFT + CENTER: EVENT LOG */}
        <div className="col-span-2 bg-gray-800 border-t border-gray-700 flex flex-col min-h-0">
          <div className="px-3 py-1.5 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
              Event Log ({m.events.length})
            </span>
            {m.captainAlert && (
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[10px] text-red-400 font-bold uppercase">
                  Captain Required - {m.captainAlert.playerName}
                </span>
                <button
                  onClick={() => m.setCaptainAlert(null)}
                  className="text-gray-500 hover:text-gray-300 text-xs"
                >
                  ×
                </button>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {[...m.events].reverse().slice(0, 50).map((evt) => {
              const evtPlayerName = evt.player
                ? `${evt.player.last_name}`
                : '???';
              const isVAReligible = ['goal', 'penalty_goal', 'own_goal', 'penalty', 'red_card'].some(t =>
                evt.event_type?.toLowerCase().includes(t)
              );
              return (
                <div
                  key={evt.id}
                  className={`flex items-center gap-2 px-2 py-0.5 rounded-sm text-xs ${
                    evt.is_cancelled ? 'opacity-40 line-through' : 'hover:bg-gray-700/50'
                  }`}
                >
                  <span className="font-mono text-amber-400 w-8 text-right flex-shrink-0">
                    {evt.minute}'
                  </span>
                  <span className="text-gray-300 flex-shrink-0 w-20 truncate">
                    {evt.event_type?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  <span className="text-white font-bold truncate">
                    {evtPlayerName}
                  </span>
                  <span className="text-gray-600 text-[10px] flex-shrink-0">
                    {evt.team?.short_name || ''}
                  </span>
                  {!evt.is_cancelled && isVAReligible && (
                    <button
                      onClick={() => m.setVarModal({ isOpen: true, event: evt })}
                      className="px-2 py-0.5 bg-blue-800 border border-blue-600 text-blue-200 text-[10px] font-bold rounded-sm hover:bg-blue-700 flex-shrink-0 ml-auto"
                    >
                      VAR
                    </button>
                  )}
                </div>
              );
            })}
            {m.events.length === 0 && (
              <div className="text-center text-gray-600 text-xs py-4">NO EVENTS</div>
            )}
          </div>
        </div>

        {/* BOTTOM RIGHT: CLOCK CONTROLS */}
        <div className="bg-gray-800 border-t border-gray-700 flex flex-col min-h-0">
          <div className="px-3 py-1.5 border-b border-gray-700 flex-shrink-0">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Clock Control</span>
          </div>
          <div className="flex-1 p-3 flex flex-col gap-2 overflow-y-auto">
            {/* Large clock display */}
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-400">
                {String(m.clock.minute).padStart(2, '0')}:{String(m.clock.second).padStart(2, '0')}
              </div>
              {m.clock.injuryTime > 0 && (
                <div className="text-lg text-red-400 font-bold">+{m.clock.injuryTime}</div>
              )}
            </div>

            {/* Start/Stop */}
            <div className="grid grid-cols-2 gap-1">
              <ConsoleButton
                onClick={m.handleStartClock}
                color="green"
                active={m.clock.running}
                disabled={!m.joined}
                className="py-3"
              >
                START
              </ConsoleButton>
              <ConsoleButton
                onClick={m.stopClock}
                color="red"
                active={!m.clock.running && m.clock.minute > 0}
                disabled={!m.joined}
                className="py-3"
              >
                STOP
              </ConsoleButton>
            </div>

            {/* Reset */}
            <ConsoleButton
              onClick={m.resetClock}
              color="gray"
              disabled={!m.joined}
              className="py-2 w-full"
            >
              RESET
            </ConsoleButton>

            {/* Half selector */}
            <div className="grid grid-cols-4 gap-1">
              {[
                { half: 1, label: '1ST' },
                { half: 2, label: '2ND' },
                { half: 3, label: 'ET1' },
                { half: 4, label: 'ET2' }
              ].map(({ half, label }) => (
                <ConsoleButton
                  key={half}
                  onClick={() => m.changeHalf(half)}
                  color="amber"
                  active={m.clock.half === half}
                  disabled={!m.joined}
                  className="py-2"
                >
                  {label}
                </ConsoleButton>
              ))}
            </div>

            {/* Injury time */}
            <div>
              <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Injury Time</span>
              <div className="grid grid-cols-4 gap-1">
                {[1, 2, 3, 5].map(min => (
                  <ConsoleButton
                    key={min}
                    onClick={() => m.setInjuryTime(min)}
                    color="red"
                    active={m.clock.injuryTime === min}
                    disabled={!m.joined}
                    className="py-2"
                  >
                    +{min}
                  </ConsoleButton>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Modals */}
    <ConfirmModal
      isOpen={m.confirmModal.isOpen}
      title={m.confirmModal.title}
      message={m.confirmModal.message}
      confirmText={m.confirmModal.action === 'start' ? 'Start Match' : m.confirmModal.action === 'end' ? 'End Match' : 'Cancel Match'}
      confirmClass={
        m.confirmModal.action === 'start' ? 'bg-green-600 hover:bg-green-700' :
        m.confirmModal.action === 'end' ? 'bg-blue-600 hover:bg-blue-700' :
        'bg-red-600 hover:bg-red-700'
      }
      onConfirm={() => {
        if (m.confirmModal.action === 'start') m.handleStartMatch();
        else if (m.confirmModal.action === 'end') m.handleEndMatch();
        else if (m.confirmModal.action === 'cancel') m.handleCancelMatch();
      }}
      onCancel={() => m.setConfirmModal({ isOpen: false, action: null, title: '', message: '' })}
    />

    <SubstitutionModal
      isOpen={m.substitutionModal.isOpen}
      outgoingPlayer={m.substitutionModal.outgoingPlayer}
      benchPlayers={m.benchPlayersForSubstitution}
      onConfirm={m.handleSubstitutionConfirm}
      onCancel={() => m.setSubstitutionModal({ isOpen: false, outgoingPlayer: null })}
    />

    <CaptainTransferModal
      isOpen={m.captainTransferModal.isOpen}
      currentCaptain={m.captainTransferModal.currentCaptain}
      eligiblePlayers={m.eligiblePlayersForCaptain}
      onTransfer={m.handleCaptainTransfer}
      onCancel={() => m.setCaptainTransferModal({ isOpen: false, currentCaptain: null, pendingSubstitution: null })}
    />

    <VARReviewModal
      isOpen={m.varModal.isOpen}
      event={m.varModal.event}
      matchId={matchId}
      onClose={() => m.setVarModal({ isOpen: false, event: null })}
    />
    </>
  );
};
